import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IconAlertTriangle,
  IconBan,
  IconBell,
  IconBook2,
  IconBuilding,
  IconCircleCheck,
  IconClipboardList,
  IconClock,
  IconCrown,
  IconCurrencyDollar,
  IconDiamond,
  IconFileText,
  IconFlame,
  IconGift,
  IconHeart,
  IconHome,
  IconKey,
  IconMapPin,
  IconMessageCircle,
  IconReceipt,
  IconSeedling,
  IconSpeakerphone,
  IconStar,
  IconUsers,
  IconWorld,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useMingo } from '../context/MingoContext';
import PlacementAdCard from '../components/PlacementAdCard';
import { usePlacementAdPool } from '../hooks/usePlacementAdPool';
import { fetchMyAppUserRow } from '../api/profileApi';
import { fetchExpiryReminders } from '../api/expiryReminderApi';
import { getSupabase, isSupabaseConfigured } from '../lib/supabase';
import UserAvatar from '../components/UserAvatar';
import { shareInviteLink } from '../lib/inviteShare';

function resolveMannerTier(score) {
  const n = Number(score) || 0;
  if (n >= 91) return 'S';
  if (n >= 71) return 'A';
  if (n >= 51) return 'B';
  if (n >= 37) return 'C';
  return 'D';
}

function resolveMannerTierIcon(score) {
  const n = Number(score) || 0;
  if (n >= 91) return IconCrown;
  if (n >= 71) return IconDiamond;
  if (n >= 51) return IconFlame;
  if (n >= 37) return IconStar;
  return IconSeedling;
}

function ServiceMenuPage({ onBack, isClosing = false }) {
  const navigate = useNavigate();
  const { t, showToast, currentUser, isGuest } = useMingo();
  const serviceMenuAdPool = usePlacementAdPool('service_menu_feed_cpm', 3);
  const panelRef = useRef(null);
  const swipeStartX = useRef(0);
  const swipeStartY = useRef(0);
  const swipeActive = useRef(false);
  const swipeLastDx = useRef(0);
  const swipeMaxPan = useRef(320);


  const hasLocalSession = useMemo(() => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return false;
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const key = window.localStorage.key(i) || '';
        if (key.includes('supabase.auth.token') || key.startsWith('sb-')) return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  const showProfile = !!currentUser || hasLocalSession || !isGuest;
  const profileName = currentUser?.nickname || currentUser?.name || t('serviceMenu.profileFallback');
  const profileId = currentUser?.username || '';
  const profileAvatar = currentUser?.avatar_url || currentUser?.avatar || '';
  const followingCount = currentUser?.following_count || 0;
  const followersCount = currentUser?.followers_count || 0;
  const [stats, setStats] = useState({
    points: Number(currentUser?.points || 0),
    tradeCount: 0,
    reviewCount: 0,
    noShowCount: 0,
  });
  const [manner, setManner] = useState(
    Number.isFinite(Number(currentUser?.manner_temperature)) ? Number(currentUser.manner_temperature) : 100,
  );
  const [urgentExpiryCount, setUrgentExpiryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const sb = isSupabaseConfigured ? getSupabase() : null;
    if (!sb) return () => {};

    const countOrZero = async (promise) => {
      try {
        const { count, error } = await promise;
        if (error) return 0;
        return count ?? 0;
      } catch {
        return 0;
      }
    };

    const calcDaysUntil = (dateStr) => {
      if (!dateStr) return null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const target = new Date(dateStr);
      target.setHours(0, 0, 0, 0);
      return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
    };

    const calcMonthlyDaysUntil = (day) => {
      const n = Number(day);
      if (!Number.isFinite(n) || n < 1 || n > 31) return null;
      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth();
      const d = now.getDate();
      const thisMonthLast = new Date(y, m + 1, 0).getDate();
      const thisTarget = Math.min(thisMonthLast, n);
      if (thisTarget >= d) return thisTarget - d;
      const nextMonthLast = new Date(y, m + 2, 0).getDate();
      const nextTarget = Math.min(nextMonthLast, n);
      return thisMonthLast - d + nextTarget;
    };

    void (async () => {
      const user = await fetchMyAppUserRow();
      if (!user?.id || cancelled) return;
      const [tradeCount, reviewCount, noShowCount, expiryList] = await Promise.all([
        countOrZero(sb.from('products').select('*', { count: 'exact', head: true }).eq('author_user_id', user.id)),
        countOrZero(sb.from('guide_reviews').select('*', { count: 'exact', head: true }).eq('user_id', user.id)),
        countOrZero(sb.from('no_show_records').select('*', { count: 'exact', head: true }).eq('user_id', user.id)),
        fetchExpiryReminders(user.id).catch(() => []),
      ]);
      if (cancelled) return;
      const urgent = (expiryList || [])
        .map((item) => {
          const isMonthly = item.reminder_kind === 'monthly';
          const dday = isMonthly ? calcMonthlyDaysUntil(item.monthly_day) : calcDaysUntil(item.expiry_date);
          return { ...item, dday };
        })
        .filter((item) => Number.isFinite(item.dday) && item.dday >= 0 && item.dday <= 7);
      setStats({
        points: Number(user.points) || Number(currentUser?.points || 0),
        tradeCount,
        reviewCount,
        noShowCount,
      });
      setManner(Number.isFinite(Number(user.manner_temperature)) ? Number(user.manner_temperature) : 100);
      setUrgentExpiryCount(urgent.length);
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.points]);

  const mannerTier = resolveMannerTier(manner);
  const mannerTierIcon = resolveMannerTierIcon(manner);
  const mannerPercent = `${Math.max(0, Math.min(100, manner)).toFixed(1)}%`;

  const statsData = useMemo(
    () => [
      { id: 'points', labelKey: 'myPage.statsPoints', value: stats.points, Icon: IconCurrencyDollar, red: false },
      { id: 'trades', labelKey: 'myPage.statsTrades', value: stats.tradeCount, Icon: IconCircleCheck, red: false },
      { id: 'reviews', labelKey: 'myPage.statsReviews', value: stats.reviewCount, Icon: IconStar, red: false },
      { id: 'noshow', labelKey: 'myPage.statsNoShow', value: stats.noShowCount, Icon: IconAlertTriangle, red: stats.noShowCount > 0 },
    ],
    [stats.noShowCount, stats.points, stats.reviewCount, stats.tradeCount],
  );

  const quickMenus = useMemo(
    () => [
      { id: 'realEstate', Icon: IconHome, labelKey: 'myPage.menu.realEstateBrowse', to: '/real-estate' },
      { id: 'myProperties', Icon: IconClipboardList, labelKey: 'myPage.menu.myProperties', to: '/my-properties' },
      { id: 'myTrades', Icon: IconReceipt, labelKey: 'myPage.myTrades', to: '/my-trades' },
      { id: 'myFavorites', Icon: IconHeart, labelKey: 'myPage.myFavorites', to: '/my-favorites' },
      { id: 'myCommunityPosts', Icon: IconMessageCircle, labelKey: 'myPage.myCommunityPosts', to: '/my/community-posts' },
      { id: 'groups', Icon: IconUsers, labelKey: 'groups.title', to: '/groups' },
      { id: 'inviteFriend', Icon: IconGift, labelKey: 'myPage.invite', onClick: () => void shareInviteLink({ t, showToast }) },
    ],
    [showToast, t],
  );

  const profileMenus = useMemo(
    () => [
      {
        id: 'biz-dash',
        Icon: IconBuilding,
        label: t('myPage.menu.businessDashboard'),
        to: '/business-dashboard',
      },
      {
        id: 'ad-manage',
        Icon: IconSpeakerphone,
        label: t('myPage.menu.manageAds'),
        to: '/business-dashboard?focus=ads',
      },
      {
        id: 'notif',
        Icon: IconBell,
        label: t('myPage.menu.notificationSettings'),
        to: '/settings/notifications',
      },
      {
        id: 'blocked-users',
        Icon: IconBan,
        label: t('myPage.menu.blockedUsers'),
        to: '/settings/blocked-users',
      },
      {
        id: 'expiry',
        Icon: IconClock,
        label: t('expiry.menuLabel'),
        badge: urgentExpiryCount > 0 ? (urgentExpiryCount > 99 ? '99+' : String(urgentExpiryCount)) : null,
        to: '/expiry-reminder',
      },
      {
        id: 'lang',
        Icon: IconWorld,
        label: t('myPage.menu.language'),
        to: '/onboarding/language?mode=settings',
      },
      {
        id: 'region',
        Icon: IconMapPin,
        label: t('myPage.menu.region'),
        to: '/onboarding/location-select?mode=settings',
      },
      { id: 'privacy', Icon: IconFileText, label: t('privacy.title'), to: '/privacy' },
      { id: 'terms', Icon: IconBook2, label: t('terms.title'), to: '/terms' },
    ],
    [t, urgentExpiryCount],
  );

  const goFromProfileMenu = useCallback(
    (to) => {
      onBack();
      window.queueMicrotask(() => navigate(to));
    },
    [navigate, onBack],
  );

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return undefined;

    const onStart = (e) => {
      if (e.touches?.length !== 1) return;
      swipeActive.current = true;
      swipeStartX.current = e.touches[0].clientX;
      swipeStartY.current = e.touches[0].clientY;
      swipeLastDx.current = 0;
      swipeMaxPan.current = Math.min(el.offsetWidth, typeof window !== 'undefined' ? window.innerWidth : 400);
      el.style.transition = 'none';
    };
    const onMove = (e) => {
      if (!swipeActive.current || e.touches?.length !== 1) return;
      const dx = e.touches[0].clientX - swipeStartX.current;
      const dy = e.touches[0].clientY - swipeStartY.current;
      swipeLastDx.current = dx;
      if (dx >= 0) {
        el.style.transform = '';
        return;
      }
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
        const capped = Math.max(dx, -swipeMaxPan.current);
        el.style.transform = `translate3d(${capped}px,0,0)`;
        e.preventDefault();
      }
    };
    const onEnd = () => {
      if (!swipeActive.current) return;
      swipeActive.current = false;
      const dx = swipeLastDx.current;
      const max = swipeMaxPan.current;
      const close = dx < -72 || dx < -max * 0.22;
      el.style.transition = 'transform 0.22s ease-out';
      if (close) {
        el.style.transform = `translate3d(${-max - 24}px,0,0)`;
        window.setTimeout(() => {
          el.style.transform = '';
          onBack();
        }, 200);
      } else {
        el.style.transform = '';
        window.setTimeout(() => {
          el.style.transition = '';
        }, 220);
      }
      swipeLastDx.current = 0;
    };

    const opts = { passive: false };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, opts);
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [onBack]);

  return (
    <div
      className={`mingo-service-page${isClosing ? ' mingo-service-page--closing' : ''}`}
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9f7ff 0%, #fff 38%, #f2e7ff 100%)',
        overflowX: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={onBack}
        aria-label={t('productDetail.back')}
        style={{
          position: 'fixed',
          right: 14,
          top: 22,
          width: 44,
          height: 44,
          border: 0,
          background: 'transparent',
          fontSize: 40,
          lineHeight: 1,
          color: '#111',
          zIndex: 5,
          cursor: 'pointer',
        }}
      >
        ×
      </button>
      <div
        ref={panelRef}
        className="mingo-service-page__body"
        style={{
          marginRight: 'max(96px, 22vw)',
          width: 'min(360px, calc(100vw - max(96px, 22vw)))',
          minHeight: '100vh',
          background: '#fff',
          borderRight: '1px solid #ececec',
          boxShadow: '8px 0 24px rgba(17, 17, 17, 0.06)',
          padding: '14px 18px 28px',
          willChange: 'transform',
        }}
      >
        {showProfile ? (
          <section style={{ paddingTop: 4, marginBottom: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <button
                type="button"
                onClick={() => {
                  onBack();
                  /* 메뉴 닫힘과 라우트 전환을 한 틱 분리 — 전체서비스 → 내정보 전환 시 렌더 경합 방지 */
                  window.queueMicrotask(() => navigate('/settings/account'));
                }}
                aria-label={t('serviceMenu.ariaProfile')}
                style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer', flexShrink: 0 }}
              >
                <UserAvatar src={profileAvatar} name={profileName} size={44} />
              </button>
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 16, fontWeight: 900, color: '#111' }}>{profileName}</p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#666' }}>{profileId ? `@${profileId}` : ''}</p>
                <p style={{ margin: '4px 0 0', fontSize: 11, color: '#666' }}>
                  {t('serviceMenu.followCounts', { following: followingCount, followers: followersCount })}
                </p>
                <div style={{ marginTop: 5, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span className="mingo-service-tier-badge" aria-label={`${t('myPage.mannerTier')} ${mannerTier}`}>
                    <span className="mingo-service-tier-badge__icon">{mannerTierIcon ? React.createElement(mannerTierIcon, { size: 14, stroke: 2, 'aria-hidden': true }) : null}</span>
                    <span className="mingo-service-tier-badge__text">{mannerTier}</span>
                  </span>
                  <span style={{ fontSize: 11, color: '#4b5563', fontWeight: 800 }}>{mannerPercent}</span>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <button
            type="button"
            onClick={() => {
              onBack();
              navigate('/onboarding/auth');
            }}
            style={{
              width: '100%',
              minHeight: 64,
              border: 0,
              borderBottom: '1px solid #e5e5e5',
              background: 'transparent',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 2px',
              fontSize: 15,
              fontWeight: 900,
              color: '#111',
              cursor: 'pointer',
              marginBottom: 8,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
              <span aria-hidden="true"><IconKey size={18} stroke={2} /></span>
              {t('serviceMenu.loginSignup')}
            </span>
            <span aria-hidden="true" style={{ fontSize: 34, lineHeight: 0.7 }}>
              ›
            </span>
          </button>
        )}

        <section className="mingo-mypage-stats" style={{ marginTop: 10 }}>
          {statsData.map((stat) => (
            <button
              key={stat.labelKey}
              type="button"
              className={`mingo-mypage-stats__item${stat.red ? ' mingo-mypage-stats__item--red' : ''}`}
            >
              <span className={`mingo-service-icon-bubble mingo-service-icon-bubble--${stat.id}`}>{stat.Icon ? React.createElement(stat.Icon, { size: 18, stroke: 2, 'aria-hidden': true }) : null}</span>
              <span className="mingo-mypage-stats__value">{Number(stat.value).toLocaleString()}</span>
              <span className="mingo-mypage-stats__label">{t(stat.labelKey)}</span>
            </button>
          ))}
        </section>

        <section className="mingo-mypage-quick" style={{ marginTop: 10 }}>
          {quickMenus.map((menu) => (
            <button
              key={menu.id}
              type="button"
              className="mingo-mypage-quick__item"
              onClick={menu.to ? () => goFromProfileMenu(menu.to) : menu.onClick}
            >
              <span className={`mingo-service-icon-bubble mingo-service-icon-bubble--${menu.id}`}>{menu.Icon ? React.createElement(menu.Icon, { size: 18, stroke: 2, 'aria-hidden': true }) : null}</span>
              <span className="mingo-mypage-quick__label">{menu.labelKey ? t(menu.labelKey) : menu.label}</span>
            </button>
          ))}
        </section>

        <section style={{ marginTop: 10 }}>
          <PlacementAdCard
            ad={serviceMenuAdPool.length ? serviceMenuAdPool[0] : null}
            variant="banner"
            slotKey="service_menu_feed_cpm"
            seed={0}
          />
        </section>

        <section className="mingo-mypage-menu" style={{ marginTop: 10 }}>
          {profileMenus.map((item) => (
            <button key={item.id} type="button" className="mingo-mypage-menu__item" onClick={() => goFromProfileMenu(item.to)}>
              <span className="mingo-mypage-menu__item-icon">{item.Icon ? React.createElement(item.Icon, { size: 18, stroke: 2, 'aria-hidden': true }) : null}</span>
              <span className="mingo-mypage-menu__item-label">{item.label}</span>
              {item.badge ? <span className="mingo-badge mingo-badge--purple">{item.badge}</span> : null}
              <span className="mingo-mypage-menu__item-arrow">›</span>
            </button>
          ))}
        </section>

      </div>
    </div>
  );
}

export default ServiceMenuPage;
